import { useEffect, useState } from 'react';
import ilustracao from '../../assets/img_planoCorporativo.png';

import {
    X,
    Zap,
    Rocket,
    Shield,
    BarChart3,
    Users,
    ArrowRight,
    Phone,
    CheckCircle2,
    Clock,
    AlertCircle,
} from 'lucide-react';

import { corporateContracts } from '../../services/authService';
import './GoldSubscriptionCompany.scss';

export default function PremiumPlanModal({ isOpen, onClose }) {
    const [myContract, setMyContract] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isOpen) return;

        async function fetchMyContract() {
            setLoading(true);

            try {
                const data = await corporateContracts.getMine();
                setMyContract(data);
            } catch (error) {
                console.error('Erro ao buscar contrato empresarial:', error);
                setMyContract(null);
            } finally {
                setLoading(false);
            }
        }

        fetchMyContract();
    }, [isOpen]);

    if (!isOpen) return null;

    const handleWhatsapp = () => {
        const mensagem = encodeURIComponent(
            'Olá! Tenho interesse em conhecer o plano corporativo para minha empresa.'
        );

        window.open(
            `https://wa.me/5511996653731?text=${mensagem}`,
            '_blank',
            'noopener,noreferrer'
        );
    };

    const statusConfig = {
        Pending: {
            label: 'Aguardando 1ª cobrança',
            icon: Clock,
            color: '#f59e0b',
            background: '#fffbeb',
            border: '#fde68a',
        },
        Active: {
            label: 'Plano ativo',
            icon: CheckCircle2,
            color: '#059669',
            background: '#ecfdf5',
            border: '#a7f3d0',
        },
        Free: {
            label: 'Bloqueado — saldo insuficiente',
            icon: AlertCircle,
            color: '#dc2626',
            background: '#fef2f2',
            border: '#fecaca',
        },
        Suspended: {
            label: 'Plano suspenso',
            icon: AlertCircle,
            color: '#6b7280',
            background: '#f8fafc',
            border: '#e2e8f0',
        },
    };

    /*
     * ============================================================
     * EMPRESA JÁ POSSUI CONTRATO
     * ============================================================
     */

    if (!loading && myContract) {
        const status =
            statusConfig[myContract.status] ?? statusConfig.Pending;

        const StatusIcon = status.icon;

        return (
            <div className="ppm-overlay" onClick={onClose}>
                <div
                    className="ppm-modal ppm-modal-contract"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        className="ppm-close"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <X size={22} />
                    </button>

                    <div className="ppm-contract-content">
                        <div className="ppm-contract-header">
                            <div className="ppm-badge">
                                <Users size={14} />
                                SEU PLANO CORPORATIVO
                            </div>

                            <h2 className="ppm-title">
                                {myContract.contractName}
                            </h2>

                            <p className="ppm-contract-description">
                                Confira os detalhes e benefícios do plano
                                corporativo da sua empresa.
                            </p>
                        </div>

                        <div
                            className="ppm-contract-status"
                            style={{
                                background: status.background,
                                borderColor: status.border,
                            }}
                        >
                            <div
                                className="ppm-contract-status-icon"
                                style={{
                                    color: status.color,
                                    background: `${status.color}15`,
                                }}
                            >
                                <StatusIcon size={22} />
                            </div>

                            <div className="ppm-contract-status-content">
                                <span
                                    className="ppm-contract-status-label"
                                    style={{ color: status.color }}
                                >
                                    Status do plano
                                </span>

                                <h3>{status.label}</h3>

                                {myContract.status === 'Free' && (
                                    <p>
                                        Adicione saldo à carteira para
                                        reativar os benefícios do seu plano.
                                    </p>
                                )}

                                {myContract.status === 'Pending' && (
                                    <p>
                                        Seu contrato está cadastrado e
                                        aguardando a primeira cobrança.
                                    </p>
                                )}

                                {myContract.status === 'Suspended' && (
                                    <p>
                                        Entre em contato com nosso time
                                        comercial para verificar sua situação.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="ppm-contract-grid">
                            <div className="ppm-contract-card">
                                <div className="ppm-contract-card-top">
                                    <div className="ppm-contract-card-icon">
                                        <BarChart3 size={18} />
                                    </div>

                                    <span>Valor mensal</span>
                                </div>

                                <strong>
                                    R${' '}
                                    {Number(
                                        myContract.monthlyValue ?? 0
                                    ).toFixed(2)}
                                </strong>

                                <p>
                                    Cobrado todo dia{' '}
                                    <strong>
                                        {myContract.billingDay}
                                    </strong>{' '}
                                    de cada mês.
                                </p>
                            </div>

                            <div className="ppm-contract-card">
                                <div className="ppm-contract-card-top">
                                    <div className="ppm-contract-card-icon">
                                        <Zap size={18} />
                                    </div>

                                    <span>Taxa da plataforma</span>
                                </div>

                                {Number(myContract.feePercentage) === 0 ? (
                                    <>
                                        <strong className="ppm-green">
                                            ISENTO — 0%
                                        </strong>

                                        <p>
                                            Sua empresa não paga taxa sobre as
                                            contratações de freelancers.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <strong>
                                            {myContract.feePercentage}%
                                        </strong>

                                        <p>
                                            Aplicada sobre o valor de cada
                                            contratação de freelancer.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="ppm-enabled-benefits">
                            <div className="ppm-section-title">
                                <span>Benefícios inclusos</span>
                            </div>

                            <div className="ppm-enabled-benefits-list">
                                {myContract.jobPriorityEnabled && (
                                    <div className="ppm-enabled-benefit">
                                        <div className="ppm-enabled-benefit-icon">
                                            <Rocket size={18} />
                                        </div>

                                        <div>
                                            <h4>Vagas com prioridade</h4>
                                            <p>
                                                Suas vagas recebem maior
                                                destaque para freelancers.
                                            </p>
                                        </div>

                                        <CheckCircle2
                                            className="ppm-benefit-check"
                                            size={18}
                                        />
                                    </div>
                                )}

                                {myContract.punishmentAnalysisEnabled && (
                                    <div className="ppm-enabled-benefit">
                                        <div className="ppm-enabled-benefit-icon">
                                            <Shield size={18} />
                                        </div>

                                        <div>
                                            <h4>
                                                Análise e suporte nas
                                                contratações
                                            </h4>
                                            <p>
                                                Mais segurança e suporte em
                                                situações envolvendo
                                                freelancers.
                                            </p>
                                        </div>

                                        <CheckCircle2
                                            className="ppm-benefit-check"
                                            size={18}
                                        />
                                    </div>
                                )}

                                {!myContract.jobPriorityEnabled &&
                                    !myContract.punishmentAnalysisEnabled && (
                                        <div className="ppm-no-benefits">
                                            <AlertCircle size={18} />
                                            <span>
                                                Nenhum benefício adicional
                                                configurado neste contrato.
                                            </span>
                                        </div>
                                    )}
                            </div>
                        </div>

                        <button
                            className="ppm-submit-btn"
                            onClick={handleWhatsapp}
                        >
                            Falar com o time comercial
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /*
     * ============================================================
     * EMPRESA AINDA NÃO POSSUI CONTRATO
     * ============================================================
     */

    const benefits = [
        {
            icon: <Zap size={20} />,
            title: 'Sem taxa de 10% por contratação',
            description:
                'Empresas do plano corporativo possuem condições especiais, reduzindo custos em cada freelancer contratado.',
        },
        {
            icon: <Rocket size={20} />,
            title: 'Vagas com prioridade máxima',
            description:
                'Suas vagas recebem maior visibilidade e ficam em destaque para freelancers disponíveis.',
        },
        {
            icon: <Shield size={20} />,
            title: 'Análise e suporte nas contratações',
            description:
                'Nossa equipe auxilia em situações envolvendo freelancers, trazendo mais segurança para sua empresa.',
        },
        {
            icon: <BarChart3 size={20} />,
            title: 'Plano personalizado',
            description:
                'Criamos uma proposta conforme sua demanda de freelancers, volume de contratações e necessidade da empresa.',
        },
    ];

    return (
        <div className="ppm-overlay" onClick={onClose}>
            <div
                className="ppm-modal ppm-modal-sales"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className="ppm-close"
                    onClick={onClose}
                    aria-label="Fechar"
                >
                    <X size={22} />
                </button>

                <div className="ppm-grid">
                    <div className="ppm-image-section">
                        <div className="ppm-image-wrapper">
                            <img
                                src={ilustracao}
                                className="ppm-image"
                                alt="Plano corporativo"
                            />

                            <div className="ppm-image-badge">
                                <span>⭐ Exclusivo</span>
                            </div>
                        </div>

                        <div className="ppm-image-features">
                            <div className="ppm-image-feature">
                                <CheckCircle2 size={16} />
                                <span>Suporte dedicado</span>
                            </div>

                            <div className="ppm-image-feature">
                                <CheckCircle2 size={16} />
                                <span>Condições especiais</span>
                            </div>

                            <div className="ppm-image-feature">
                                <CheckCircle2 size={16} />
                                <span>Plano sob medida</span>
                            </div>
                        </div>
                    </div>

                    <div className="ppm-content">
                        <div className="ppm-header">
                            <div className="ppm-badge">
                                <Users size={14} />
                                PLANO CORPORATIVO
                            </div>

                            <h2 className="ppm-title">
                                Uma solução personalizada para sua empresa
                            </h2>

                            <p className="ppm-subtitle">
                                Criamos um plano sob medida conforme sua
                                demanda de freelancers.
                            </p>
                        </div>

                        <div className="ppm-benefits">
                            {benefits.map((benefit, index) => (
                                <div
                                    key={index}
                                    className="ppm-benefit"
                                >
                                    <div className="ppm-benefit-icon">
                                        {benefit.icon}
                                    </div>

                                    <div className="ppm-benefit-content">
                                        <h4>{benefit.title}</h4>
                                        <p>{benefit.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="ppm-contact-box">
                            <div className="ppm-contact-icon">
                                <Phone size={22} />
                            </div>

                            <div>
                                <h3>Fale com nosso time comercial</h3>

                                <p>
                                    Vamos entender sua necessidade e montar a
                                    melhor proposta.
                                </p>
                            </div>
                        </div>

                        <button
                            className="ppm-submit-btn"
                            onClick={handleWhatsapp}
                        >
                            Conversar pelo WhatsApp
                            <ArrowRight size={18} />
                        </button>

                        <button
                            className="ppm-later-btn"
                            onClick={onClose}
                        >
                            Talvez mais tarde
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}